import { Link, StatusIndicator } from '@cloudscape-design/components';
import _ from 'lodash';
import FilterDatePicker, {
  createDateFilterConfig,
} from 'src/components/filter/FilterDatePicker';
import { FILTER_OPERATORS } from 'src/contstant/FilterTypes';
import { FilterItemType } from 'src/hooks/useGenericFilter';
import { formatDateTime } from 'src/utils/generalUtils';
import { ExportFileConfig } from 'src/utils/exportFileUtils';
import { AuditTemplate } from '@amzn/gbsa-helios-typescript-client';

export enum TemplatesPropertyKey {
  TEMPLATES_ACTIVE_STATUS = 'parent.templateActiveStatus',
  LAST_UPDATED_AT = 'parent.LastUpdatedAt',
  CREATED_AT = 'parent.createdAt',
}

// Initial filter options
export const initialFilters = [
  {
    propertyKey: TemplatesPropertyKey.TEMPLATES_ACTIVE_STATUS,
    value: 'true',
    label: 'Active',
  },
  {
    propertyKey: TemplatesPropertyKey.TEMPLATES_ACTIVE_STATUS,
    value: 'false',
    label: 'Inactive',
  },
];

export const getItemValue = (item: AuditTemplate, propertyKey: string): any => {
  if (!item) return '';

  switch (propertyKey) {
    case TemplatesPropertyKey.TEMPLATES_ACTIVE_STATUS:
      return {
        itemValue: item?.isActive,
        type: FilterItemType.BOOLEAN,
      };

    case TemplatesPropertyKey.LAST_UPDATED_AT:
      return {
        itemValue: item?.lastUpdatedAt,
        type: FilterItemType.DATE,
      };
    case TemplatesPropertyKey.CREATED_AT:
      return {
        itemValue: item?.createdAt,
        type: FilterItemType.DATE,
      };
    default:
      return { itemValue: '' };
  }
};

export const getSearchableFields = (item: AuditTemplate) => [
  item.templateName || '',
  item.createdBy || '',
  item.lastUpdatedBy || '',
  ...Object.keys(item.tags || {}),
];

export const getFilterTemplatesFilterProperties = (): any[] => {
  const baseFilters: any[] = [
    {
      key: TemplatesPropertyKey.TEMPLATES_ACTIVE_STATUS,
      propertyLabel: 'Active Status',
      operators: [FILTER_OPERATORS.EQUALS, FILTER_OPERATORS.NOT_EQUALS],
      groupValuesLabel: 'Active values',
    },
    createDateFilterConfig({
      key: TemplatesPropertyKey.LAST_UPDATED_AT,
      propertyLabel: 'Updated At',
      groupValuesLabel: 'Date',
    }),
    createDateFilterConfig({
      key: TemplatesPropertyKey.CREATED_AT,
      propertyLabel: 'Created At',
      groupValuesLabel: 'Date',
    }),
  ];

  return baseFilters;
};
export const defaultVisibleColumns = [
  'id',
  'name',
  'divisions',
  'isActive',
  'lastUpdatedAt',
];
export const getTemplatesTableDefinition = () => {
  return [
    {
      id: 'id',
      header: 'Template ID',
      cell: (item: AuditTemplate) => item.id,
    },
    {
      id: 'name',
      header: 'Name',
      cell: (item: AuditTemplate) => <Link>{item.templateName}</Link>,
    },
    {
      id: 'divisions',
      header: 'Divisions',
      cell: (item: AuditTemplate) => item.divisionName,
    },
    {
      id: 'tags',
      header: 'Tags',
      cell: (item: AuditTemplate) => Object.keys(item.tags || {}).join(', '),
    },

    {
      id: 'locations',
      header: 'Locations',
      cell: (item: AuditTemplate) => item.locationsIncluded?.length || 0,
    },

    {
      id: 'isActive',
      header: 'Status',
      cell: (item: AuditTemplate) => {
        const status = item.isActive;
        return (
          <StatusIndicator type={status ? 'success' : 'error'}>
            {status ? 'Active' : 'Inactive'}
          </StatusIndicator>
        );
      },
    },
    {
      id: 'createdBy',
      header: 'Created By',
      cell: (item: AuditTemplate) => item.createdBy || '-',
    },
    {
      id: 'createdAt',
      header: 'Created At',
      cell: (item: AuditTemplate) => formatDateTime(item?.createdAt as any) || '-',
    },
    {
      id: 'lastUpdatedBy',
      header: 'Updated By',
      cell: (item: AuditTemplate) => item.lastUpdatedBy || '-',
    },
    {
      id: 'lastUpdatedAt',
      header: 'Updated At',
      cell: (item: AuditTemplate) => formatDateTime(item?.lastUpdatedAt as any) || '-',
    },
  ];
};

export const exportTemplatesConfig: ExportFileConfig = {
  fileName: 'templates_list.csv',
  mappings: {
    'Template ID': (template: AuditTemplate) => template.id,
    'Name': (template: AuditTemplate) => template.templateName,
    'Description': (template: AuditTemplate) => template.description ?? '',
    'Division': (template: AuditTemplate) => template.divisionName || '-',

    'Business Unit': (template: AuditTemplate) => template.businessUnit,
    'Program': (template: AuditTemplate) => template.program || '-',
    'Locations Count': (template: AuditTemplate) =>
      (template.locationsIncluded?.length || 0).toString(),

    'Tags': (template: AuditTemplate) => {
      return Object.entries(template.tags)
        .map(([tagGroupName, tagValues]) => {
          return tagValues.map((data) => `${tagGroupName} - ${data}`).join('; ');
        })
        .join('; ');
    },
    'Status': (template: AuditTemplate) => (template.isActive ? 'Active' : 'Inactive'),
    'Created By': (template: AuditTemplate) => template.createdBy || '-',
    'Created Date': (template: AuditTemplate) =>
      formatDateTime(template?.createdAt as any) || '-',
    'Updated By': (template: AuditTemplate) => template.lastUpdatedBy || '-',
    'Updated Date': (template: AuditTemplate) =>
      formatDateTime(template?.lastUpdatedAt as any) || '-',
  },
};
